import { createAction } from '@reduxjs/toolkit'
import { call, put, select, takeLatest } from 'redux-saga/effects'

import { updateDocumentMapping } from '../../../api/takeoff-api'
import { DocumentChunk } from '../../../models/documentChunk'
import { DocumentMapping } from '../../../models/documentMapping'
import {
    selectDocumentChunks,
    selectDocumentMappings,
    updateActiveChunk,
    updateDocumentMappings,
} from '../../slices/documents'

export const completeTemplatePageAction = createAction<number>('completeTemplatePageAction')

export function* handleCompleteTemplatePageAction({ payload }: ReturnType<typeof completeTemplatePageAction>) {
    const documentMappings: DocumentMapping[] | null = yield select(selectDocumentMappings)
    const documentChunks: DocumentChunk[] | null = yield select(selectDocumentChunks)

    if (!documentMappings || !documentChunks) return

    const mapping = documentMappings.find((doc) => doc.id === payload)
    const chunk = documentChunks.find((chunk) => chunk.id === mapping?.document_chunk_id)

    if (!mapping || !chunk) return

    const { buildingPages, otherPages } = documentMappings.reduce(
        (mappings, page) => {
            if (
                page.id !== payload &&
                page.is_option === false &&
                !page.additional_data.parentDocumentMapping &&
                page.is_estimation_complete === false
            ) {
                mappings[page.building_id === mapping.building_id ? 'buildingPages' : 'otherPages'].push(page)
            }

            return {
                buildingPages: mappings.buildingPages.sort((a, b) => a.id - b.id),
                otherPages: mappings.otherPages.sort((a, b) => a.id - b.id),
            }
        },
        { buildingPages: [] as DocumentMapping[], otherPages: [] as DocumentMapping[] }
    )

    const sortedTemplateOrUnclassifiedPages = [...buildingPages, ...otherPages]

    try {
        yield call(updateDocumentMapping, chunk.project_document.project_id, {
            ...mapping,
            is_estimation_complete: true,
            updated_at: undefined,
            created_at: undefined,
            deleted_at: undefined,
        } as DocumentMapping)

        // Update the mapping in the store
        yield put(
            updateDocumentMappings([
                {
                    ...mapping,
                    is_estimation_complete: true,
                },
            ])
        )
    } catch (e) {
        yield call(console.error, e)
        yield put(updateDocumentMappings([mapping]))
    }

    if (sortedTemplateOrUnclassifiedPages.length === 0) {
        yield put(updateActiveChunk(null))

        return
    }

    const nextDocumentChunk = documentChunks.find(
        (chunk) => chunk.id === sortedTemplateOrUnclassifiedPages[0].document_chunk_id
    )

    if (!nextDocumentChunk) return

    yield put(updateActiveChunk(nextDocumentChunk))
}

export function* watchForCompleteTemplatePage() {
    yield takeLatest(completeTemplatePageAction.type, handleCompleteTemplatePageAction)
}
